import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchCampusDetails } from "../../../redux/campusesSlice";
import toast from "react-hot-toast";

import ViewCoursesReportModal from "./ViewCoursesReportModal";

const CoursesReport = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [viewReportOpen, setViewReportOpen] = useState(false);
  const [selectedCampusId, setSelectedCampusId] = useState("");
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [sessionStart, setSessionStart] = useState("");
  const [sessionEnd, setSessionEnd] = useState("");
  
  const { campusDetails, loading } = useSelector((state) => state.campuses);
  
  // Dummy campus data - replace with actual data from Redux store
  const dummyCampusData = [
    { id: "campus1", name: "Main Campus Downtown" },
    { id: "campus2", name: "North Campus" },
    { id: "campus3", name: "South Campus" },
    { id: "campus4", name: "East Campus" }
  ];

  // Dummy courses data - replace with actual data from Redux store
  const dummyCoursesData = {
    "campus1": [
      { id: "course1", name: "Mathematics Grade 5", code: "MATH-5" },
      { id: "course2", name: "English Grade 5", code: "ENG-5" },
      { id: "course3", name: "Science Grade 5", code: "SCI-5" },
      { id: "course4", name: "Social Studies Grade 5", code: "SS-5" },
      { id: "course5", name: "Computer Science", code: "CS-101" }
    ],
    "campus2": [
      { id: "course6", name: "Mathematics Grade 6", code: "MATH-6" },
      { id: "course7", name: "English Grade 6", code: "ENG-6" },
      { id: "course8", name: "Physics", code: "PHY-101" },
      { id: "course9", name: "Chemistry", code: "CHEM-101" },
      { id: "course10", name: "Biology", code: "BIO-101" }
    ],
    "campus3": [
      { id: "course11", name: "Advanced Mathematics", code: "ADV-MATH" },
      { id: "course12", name: "Literature", code: "LIT-101" },
      { id: "course13", name: "History", code: "HIST-101" },
      { id: "course14", name: "Geography", code: "GEO-101" },
      { id: "course15", name: "Art & Design", code: "ART-101" }
    ]
  };
  
  const selectedCampus = campusDetails.find(campus => campus.id === selectedCampusId) || {};
  const availableCourses = dummyCoursesData[selectedCampusId] || [];

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCampusDetails());
    }
  }, [isOpen, dispatch]);
  
  // Reset dependent fields when campus changes
  useEffect(() => {
    if (selectedCampusId === "") {
      setSelectedCourses([]);
      setSessionStart("");
      setSessionEnd("");
    }
  }, [selectedCampusId]);

  const handleCourseSelection = (courseId) => {
    setSelectedCourses(prev => {
      if (prev.includes(courseId)) {
        // Remove if already selected
        return prev.filter(id => id !== courseId);
      } else {
        // Add if not selected
        return [...prev, courseId];
      }
    });
  };

  const handleSelectAllCourses = () => {
    if (selectedCourses.length === availableCourses.length) {
      // Deselect all
      setSelectedCourses([]);
    } else {
      // Select all
      setSelectedCourses(availableCourses.map(course => course.id));
    }
  };

  const handleViewReport = () => {
    if (!selectedCampusId) {
      toast.error("Please select a campus");
      return;
    }
    if (selectedCourses.length === 0) {
      toast.error("Please select at least one course");
      return;
    }
    if (!sessionStart || !sessionEnd) {
      toast.error("Please select both session start and end dates");
      return;
    }
    if (new Date(sessionStart) > new Date(sessionEnd)) {
      toast.error("Session start date cannot be later than end date");
      return;
    }

    setViewReportOpen(true);
    toast.success(`Courses report generated for ${selectedCourses.length} courses from ${sessionStart} to ${sessionEnd}`);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white p-6 rounded-lg shadow-lg w-[70%] max-h-[90vh] overflow-y-auto relative">
          <h2 className="text-xl font-semibold mb-4">Courses Report</h2>
          
          {/* Campus Selection */}
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label htmlFor="campus" className="block mb-2">
                Select Campus
              </label>
              <select 
                id="campus" 
                className="border p-2 rounded w-full"
                value={selectedCampusId}
                onChange={(e) => setSelectedCampusId(e.target.value)}
                disabled={loading}
              >
                <option value="" disabled>
                  Choose a campus
                </option>
                {dummyCampusData.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Course Selection - Multiple */}
          {selectedCampusId && (
            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block">
                  Select Courses (Multiple Selection)
                </label>
                <button
                  type="button"
                  onClick={handleSelectAllCourses}
                  className="text-sm bg-blue-100 hover:bg-blue-200 px-3 py-1 rounded text-blue-700"
                  disabled={loading}
                >
                  {selectedCourses.length === availableCourses.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="border rounded p-3 max-h-40 overflow-y-auto bg-gray-50">
                {availableCourses.length > 0 ? (
                  <div className="grid grid-cols-1 gap-2">
                    {availableCourses.map((course) => (
                      <label key={course.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedCourses.includes(course.id)}
                          onChange={() => handleCourseSelection(course.id)}
                          className="w-4 h-4"
                          disabled={loading}
                        />
                        <span className="text-sm">
                          <span className="font-medium">{course.name}</span>
                          <span className="text-gray-500 ml-1">({course.code})</span>
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No courses available for this campus</p>
                )}
              </div>
              {selectedCourses.length > 0 && (
                <p className="text-sm text-blue-600 mt-1">
                  {selectedCourses.length} course{selectedCourses.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          )}

          {/* Session Date Range */}
          {selectedCourses.length > 0 && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label htmlFor="sessionStart" className="block mb-2">
                  Session Start Date
                </label>
                <input
                  type="date"
                  id="sessionStart"
                  className="border p-2 rounded w-full"
                  value={sessionStart}
                  onChange={(e) => setSessionStart(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div>
                <label htmlFor="sessionEnd" className="block mb-2">
                  Session End Date
                </label>
                <input
                  type="date"
                  id="sessionEnd"
                  className="border p-2 rounded w-full"
                  value={sessionEnd}
                  onChange={(e) => setSessionEnd(e.target.value)}
                  min={sessionStart}
                  disabled={loading}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="bg-gray-400 px-4 py-2 rounded text-white"
            >
              Close
            </button>
            <button
              onClick={handleViewReport}
              className="bg-[#241763] px-4 py-2 rounded text-white"
              disabled={
                loading || 
                !selectedCampusId || 
                selectedCourses.length === 0 || 
                !sessionStart || 
                !sessionEnd
              }
            >
              Generate Courses Report
            </button>
          </div>
        </div>
      </div>

      <ViewCoursesReportModal
        isOpen={viewReportOpen}
        onClose={() => setViewReportOpen(false)}
        campusId={selectedCampusId}
        courseIds={selectedCourses}
        sessionStart={sessionStart}
        sessionEnd={sessionEnd}
        campusName={selectedCampus.name}
      />
    </>
  );
};

export default CoursesReport;